import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HistoryIcon from '@mui/icons-material/History';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ApartmentIcon from '@mui/icons-material/Apartment';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import {
  projectEssentialApi,
  type PeRecentSearchItem,
  type ProjectFilters,
  type UniversalSearchGroup,
  type UniversalSearchItem,
} from '../services/api';
import { getCachedAnalyticsCea } from '../services/analytics';
import {
  buildPeSearchHref,
  buildPeSearchPageHref,
  countPeFilters,
  hasPeProjectFilter,
  stashPePendingPreview,
  type PePendingPreview,
} from '../utils/searchState';

// ---------------------------------------------------------------------------
// SearchResultsDropdown
//
// Universal search-suggestions popover for Project Essentials. Calls
// `GET /search` once per query/filter change and renders the ordered
// groups (Projects, Upcoming GLS, EDMs, Client Engagement eBooks,
// Project Spotlight Ebooks, Insights EDM Reports, Analytics Files).
//
// With an empty query and no filters, shows the agent's top 5 recent
// selections (latest first) from `GET /search/recent`.
//
// Layout: a sticky header with the total count plus one chip per group
// (each with its own count), then a flat list of every matching hit.
// Results are always visible — no expand click — and the chip row keeps
// every other group discoverable even once the list is scrolled.
// Selecting a chip narrows the list to that group; "All" restores the
// mixed view.
//
// Each group gets a sticky section header carrying its name, count and a
// "View full results" link that opens the universal search results page
// with the current query and filters. The header pins under the scope
// row so both stay reachable while scrolling a long group.
// ---------------------------------------------------------------------------

export type SearchResultItem = UniversalSearchItem;

export interface SearchResultsDropdownProps {
  /** Trimmed search query. The dropdown shows recent searches when empty. */
  query: string;
  /** Filter state shared with the rest of the page. */
  filters: ProjectFilters;
  /**
   * Called when the user picks a result or a group's full-results link.
   * `pendingPreview` is set for previewable hits so the destination
   * can open FilePreviewDialog via navigate state.
   */
  onSelect: (
    href: string,
    item: SearchResultItem | null,
    pendingPreview?: PePendingPreview | null,
  ) => void;
  /**
   * Cap the scrollable panel height. PageSearchBar passes a
   * visualViewport-aware value on mobile so results stay above the
   * soft keyboard.
   */
  maxHeight?: number;
}

const ALL_GROUP_ID = '__all__';

function projectFiltersKey(filters: ProjectFilters): string {
  return [
    filters.regions?.join(',') ?? '',
    filters.districts?.join(',') ?? '',
    filters.propertyTypes?.join(',') ?? '',
    filters.projectIds?.join(',') ?? '',
    filters.soleMarketing ? '1' : '0',
  ].join(';');
}

function kindIcon(kind: string) {
  switch (kind) {
    case 'edm':
      return DescriptionOutlinedIcon;
    case 'ebook':
    case 'highlight':
      return MenuBookOutlinedIcon;
    case 'file':
      return InsertDriveFileOutlinedIcon;
    case 'gls':
      return EventAvailableIcon;
    case 'project':
    default:
      return ApartmentIcon;
  }
}

function kindLabel(kind: string): string {
  switch (kind) {
    case 'edm':
      return 'EDM';
    case 'ebook':
      return 'eBook';
    case 'highlight':
      return 'Highlight';
    case 'file':
      return 'Analytics file';
    case 'gls':
      return 'Upcoming GLS';
    case 'project':
      return 'Project';
    default:
      return kind;
  }
}

function isNavigateOnlyKind(kind: SearchResultItem['kind']): boolean {
  return kind === 'project' || kind === 'gls';
}

function isSearchKind(value: string): value is SearchResultItem['kind'] {
  return (
    value === 'project' ||
    value === 'gls' ||
    value === 'edm' ||
    value === 'ebook' ||
    value === 'highlight' ||
    value === 'file'
  );
}

function recordRecentFromItem(item: SearchResultItem, href: string) {
  void projectEssentialApi
    .recordRecentSearch({
      itemType: item.kind,
      itemId: String(item.id),
      title: item.title,
      thumbnail: item.thumbnail,
      href,
    })
    .catch(() => {
      /* non-blocking */
    });
}

function recordRecentRow(
  recent: Pick<PeRecentSearchItem, 'itemType' | 'itemId' | 'title' | 'thumbnail'>,
  href: string,
) {
  void projectEssentialApi
    .recordRecentSearch({
      itemType: recent.itemType,
      itemId: recent.itemId,
      title: recent.title,
      thumbnail: recent.thumbnail,
      href,
    })
    .catch(() => {
      /* non-blocking */
    });
}

/** Same portal DownloadEDM shape the search API returns for EDM-backed hits. */
function buildRecentEdmDownloadUrl(edmId: string): string {
  const cea = getCachedAnalyticsCea()?.trim();
  const id = String(edmId || '').trim();
  if (!id || !cea) return '#';
  const params = new URLSearchParams({ userid: cea, mode: 'admin' });
  return `https://portal.huttonsgroup.com/public/DownloadEDM/${encodeURIComponent(id)}?${params.toString()}`;
}

/**
 * Full match count for a group. `total` is the server-side count, which
 * can exceed the truncated `items` payload.
 */
function groupCount(group: UniversalSearchGroup): number {
  return group.total > 0 ? group.total : group.items.length;
}

export default function SearchResultsDropdown({
  query,
  filters,
  onSelect,
  maxHeight = 440,
}: SearchResultsDropdownProps) {
  const [groups, setGroups] = useState<UniversalSearchGroup[]>([]);
  const [recentItems, setRecentItems] = useState<PeRecentSearchItem[]>([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Selected group chip, or `ALL_GROUP_ID` for the mixed preview. */
  const [selectedGroupId, setSelectedGroupId] = useState<string>(ALL_GROUP_ID);
  const filtersKey = useMemo(() => projectFiltersKey(filters), [filters]);
  const hasActiveFilters = useMemo(() => countPeFilters(filters) > 0, [filters]);
  const hasProjectFilter = useMemo(() => hasPeProjectFilter(filters), [filters]);
  const showRecent = !query && !hasActiveFilters;
  const fetchIdRef = useRef(0);
  const recentFetchIdRef = useRef(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const scopeHeaderRef = useRef<HTMLDivElement | null>(null);
  const [scopeHeaderHeight, setScopeHeaderHeight] = useState(0);

  useEffect(() => {
    if (!showRecent) return;
    const fetchId = ++recentFetchIdRef.current;
    setRecentLoading(true);
    projectEssentialApi
      .getRecentSearches()
      .then((res) => {
        if (fetchId !== recentFetchIdRef.current) return;
        setRecentItems(Array.isArray(res.items) ? res.items : []);
      })
      .catch(() => {
        if (fetchId === recentFetchIdRef.current) setRecentItems([]);
      })
      .finally(() => {
        if (fetchId === recentFetchIdRef.current) setRecentLoading(false);
      });
  }, [showRecent]);

  useEffect(() => {
    if (!query && !hasActiveFilters) {
      setGroups([]);
      setSelectedGroupId(ALL_GROUP_ID);
      setLoading(false);
      setError(null);
      return;
    }
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);
    projectEssentialApi
      .searchContent(query, filters)
      .then((res) => {
        if (fetchId !== fetchIdRef.current) return;
        setGroups(Array.isArray(res.groups) ? res.groups : []);
        // A new result set always starts on the mixed view.
        setSelectedGroupId(ALL_GROUP_ID);
      })
      .catch((err) => {
        if (fetchId === fetchIdRef.current) {
          setError((err as Error)?.message ?? 'Search failed');
        }
      })
      .finally(() => {
        if (fetchId === fetchIdRef.current) setLoading(false);
      });
  }, [query, filters, filtersKey, hasActiveFilters]);

  const populatedGroups = useMemo(
    () =>
      groups.filter((g) => {
        if (g.items.length === 0) return false;
        if (hasProjectFilter && g.id === 'upcoming-gls') return false;
        return true;
      }),
    [groups, hasProjectFilter],
  );
  const matchingTotal = useMemo(
    () => populatedGroups.reduce((acc, g) => acc + groupCount(g), 0),
    [populatedGroups],
  );

  // Sticky group headers park directly under the scope chip header.
  useEffect(() => {
    const node = scopeHeaderRef.current;
    if (!node) {
      setScopeHeaderHeight(0);
      return;
    }
    if (typeof ResizeObserver === 'undefined') {
      setScopeHeaderHeight(node.offsetHeight);
      return;
    }
    const observer = new ResizeObserver(() => {
      setScopeHeaderHeight(node.offsetHeight);
    });
    observer.observe(node);
    setScopeHeaderHeight(node.offsetHeight);
    return () => observer.disconnect();
  }, [populatedGroups.length, matchingTotal, loading]);

  // Fall back to the mixed view if the selected group vanished from a
  // newer result set.
  const activeGroupId = populatedGroups.some((g) => g.id === selectedGroupId)
    ? selectedGroupId
    : ALL_GROUP_ID;
  const showingAll = activeGroupId === ALL_GROUP_ID;
  const visibleGroups = showingAll
    ? populatedGroups
    : populatedGroups.filter((g) => g.id === activeGroupId);

  const selectGroup = (groupId: string) => {
    setSelectedGroupId(groupId);
    // Switching scope re-tops the list so the user isn't dropped
    // mid-way down a different set of results.
    scrollRef.current?.scrollTo({ top: 0 });
  };

  const handleItemSelect = (selected: SearchResultItem) => {
    const previewUrl = (selected.url || '').trim();
    // Navigate-only kinds (projects / GLS) skip preview and keep
    // their destination href as-is.
    const canPreview = !isNavigateOnlyKind(selected.kind) && Boolean(previewUrl);

    const pendingPreview: PePendingPreview | null = canPreview
      ? {
          id: String(selected.id).trim(),
          title: selected.title,
          url: previewUrl,
          urlPublic: selected.urlPublic ?? null,
          filename: selected.filename ?? null,
          forceBlobDownload: true,
        }
      : null;

    if (pendingPreview) {
      stashPePendingPreview(pendingPreview);
    }

    const nextHref = isNavigateOnlyKind(selected.kind)
      ? selected.href
      : buildPeSearchHref(selected.href, {
          query,
          filters,
          previewId: pendingPreview?.id ?? null,
        });
    recordRecentFromItem(selected, nextHref);
    onSelect(nextHref, selected, pendingPreview);
  };

  const handleRecentSelect = (recent: PeRecentSearchItem) => {
    const storedHref = (recent.href || '').trim();
    if (!storedHref) return;

    const kind = isSearchKind(recent.itemType) ? recent.itemType : null;

    // Projects / GLS — navigate only, same as live search hits.
    if (!kind || isNavigateOnlyKind(kind)) {
      recordRecentRow(recent, storedHref);
      if (!kind) {
        onSelect(storedHref, null, null);
        return;
      }
      onSelect(
        storedHref,
        {
          id: recent.itemId,
          title: recent.title,
          subtitle: null,
          thumbnail: recent.thumbnail,
          href: storedHref,
          kind,
          createdAt: null,
          launchDate: null,
        },
        null,
      );
      return;
    }

    const existingQs = storedHref.includes('?')
      ? storedHref.slice(storedHref.indexOf('?') + 1)
      : '';
    const existingParams = new URLSearchParams(existingQs);
    // Analytics files are kind=file with no preview URL; live search
    // sends them to Highlights → Insights → Analytics.
    const isAnalyticsNavigateOnly =
      kind === 'file' && existingParams.get('section') === 'analytics';

    if (isAnalyticsNavigateOnly) {
      recordRecentRow(recent, storedHref);
      onSelect(
        storedHref,
        {
          id: recent.itemId,
          title: recent.title,
          subtitle: null,
          thumbnail: recent.thumbnail,
          href: storedHref,
          kind,
          createdAt: null,
          launchDate: null,
        },
        null,
      );
      return;
    }

    // EDM / eBook / insights report — rebuild DownloadEDM from ItemId
    // (same shape as live search), stash pending preview, navigate.
    const previewUrl = buildRecentEdmDownloadUrl(recent.itemId);
    const pendingPreview: PePendingPreview = {
      id: String(recent.itemId).trim(),
      title: recent.title,
      url: previewUrl,
      urlPublic: null,
      filename: null,
      forceBlobDownload: true,
    };
    stashPePendingPreview(pendingPreview);
    const nextHref = buildPeSearchHref(storedHref, {
      query: '',
      filters: {},
      previewId: pendingPreview.id,
    });
    recordRecentRow(recent, nextHref);
    onSelect(
      nextHref,
      {
        id: recent.itemId,
        title: recent.title,
        subtitle: null,
        thumbnail: recent.thumbnail,
        href: nextHref,
        kind,
        createdAt: null,
        launchDate: null,
        url: pendingPreview.url,
        urlPublic: null,
        filename: null,
      },
      pendingPreview,
    );
  };

  const handleViewMore = () => {
    onSelect(buildPeSearchPageHref(query, filters), null);
  };

  return (
    // `onMouseDown` preventDefault keeps the search input focused
    // while the user is interacting with the dropdown — without it
    // the popper unmounts on input blur before the click lands.
    <Box
      ref={scrollRef}
      onMouseDown={(event) => event.preventDefault()}
      sx={{
        bgcolor: 'var(--color-surface-elevated-strong)',
        backgroundImage: 'none',
        border: '1px solid var(--color-border)',
        borderRadius: 2,
        boxShadow: 'var(--color-shadow-card)',
        maxHeight,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain',
      }}
    >
      {showRecent ? (
        recentLoading && recentItems.length === 0 ? (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 2 }}>
            <CircularProgress
              size={16}
              thickness={5}
              sx={{ color: 'var(--color-accent)' }}
            />
            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
              Loading recent…
            </Typography>
          </Stack>
        ) : recentItems.length === 0 ? (
          <Typography
            variant="caption"
            sx={{ color: 'var(--color-text-muted)', display: 'block', p: 2 }}
          >
            Start typing to search projects, EDMs, eBooks, and more.
          </Typography>
        ) : (
          <>
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 2,
                bgcolor: 'var(--color-surface-elevated-strong)',
                backgroundImage: 'none',
                borderBottom: '1px solid var(--color-border)',
                px: 2,
                py: 1.25,
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: 'var(--color-text-muted)' }}
              >
                Recent
              </Typography>
            </Box>
            {recentItems.map((item) => (
              <RecentSearchRow
                key={item.id}
                item={item}
                onSelect={handleRecentSelect}
              />
            ))}
          </>
        )
      ) : loading && populatedGroups.length === 0 ? (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 2 }}>
          <CircularProgress
            size={16}
            thickness={5}
            sx={{ color: 'var(--color-accent)' }}
          />
          <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
            Searching…
          </Typography>
        </Stack>
      ) : error ? (
        <Typography
          variant="caption"
          sx={{ color: 'error.main', display: 'block', p: 2 }}
        >
          {error}
        </Typography>
      ) : populatedGroups.length === 0 ? (
        <Typography
          variant="caption"
          sx={{ color: 'var(--color-text-muted)', display: 'block', p: 2 }}
        >
          {query
            ? hasActiveFilters
              ? `No matches for "${query}" with the current filters.`
              : `No matches for "${query}".`
            : 'No results match the current filters.'}
        </Typography>
      ) : (
        <>
          {/* Sticky scope header — total plus one chip per group so every
              matched category stays visible while the list scrolls. */}
          <Box
            ref={scopeHeaderRef}
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 2,
              bgcolor: 'var(--color-surface-elevated-strong)',
              backgroundImage: 'none',
              borderBottom: '1px solid var(--color-border)',
              px: 2,
              pt: 1.25,
              pb: 1,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}
              >
                {matchingTotal} matching result{matchingTotal === 1 ? '' : 's'}
              </Typography>
              {loading && (
                <CircularProgress
                  size={12}
                  thickness={6}
                  sx={{ color: 'var(--color-accent)' }}
                />
              )}
            </Stack>
            <Stack
              direction="row"
              spacing={0.75}
              sx={{ overflowX: 'auto', pb: 0.25, '&::-webkit-scrollbar': { height: 4 } }}
            >
              <ScopeChip
                label="All"
                count={matchingTotal}
                selected={showingAll}
                onClick={() => selectGroup(ALL_GROUP_ID)}
              />
              {populatedGroups.map((group) => (
                <ScopeChip
                  key={group.id}
                  label={group.label}
                  count={groupCount(group)}
                  selected={activeGroupId === group.id}
                  onClick={() => selectGroup(group.id)}
                />
              ))}
            </Stack>
          </Box>

          <Box sx={{ pb: 0.5 }}>
            {visibleGroups.map((group) => (
              <Box key={group.id}>
                {/* Sticky section header. Carries the group's name, count
                    and its escape hatch to the owning catalogue page, so
                    scrolling a long group never loses either. */}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    position: 'sticky',
                    top: scopeHeaderHeight,
                    zIndex: 1,
                    bgcolor: 'var(--color-surface-elevated-strong)',
                    backgroundImage: 'none',
                    borderBottom: '1px solid var(--color-border)',
                    px: 2,
                    py: 0.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    {group.label} ({groupCount(group)})
                  </Typography>
                  <Button
                    size="small"
                    onClick={handleViewMore}
                    endIcon={<ArrowForwardIcon fontSize="inherit" />}
                    sx={{
                      flexShrink: 0,
                      minWidth: 0,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '0.72rem',
                      lineHeight: 1.4,
                      color: 'var(--color-accent-strong)',
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 1,
                      '&:hover': { bgcolor: 'var(--color-accent-soft)' },
                    }}
                  >
                    View full results
                  </Button>
                </Stack>
                {group.items.map((item) => (
                  <SearchResultRow
                    key={`${group.id}:${item.id}`}
                    item={item}
                    onSelect={handleItemSelect}
                  />
                ))}
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}

function ScopeChip({
  label,
  count,
  selected,
  onClick,
}: {
  label: string;
  count: number;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <Chip
      size="small"
      label={`${label} (${count})`}
      onClick={onClick}
      aria-pressed={selected}
      sx={{
        flexShrink: 0,
        borderRadius: 999,
        fontWeight: 700,
        fontSize: '0.7rem',
        border: '1px solid',
        borderColor: selected ? 'var(--color-accent)' : 'var(--color-border)',
        bgcolor: selected ? 'var(--color-accent-soft)' : 'var(--color-surface-elevated-strong)',
        color: selected ? 'var(--color-accent-strong)' : 'var(--color-text-muted)',
        '&:hover': {
          bgcolor: selected
            ? 'var(--color-accent-soft)'
            : 'color-mix(in srgb, var(--color-accent) 10%, var(--color-surface-elevated-strong))',
        },
      }}
    />
  );
}


function RecentSearchRow({
  item,
  onSelect,
}: {
  item: PeRecentSearchItem;
  onSelect: (item: PeRecentSearchItem) => void;
}) {
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onSelect(item)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(item);
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 2,
        py: 1,
        cursor: 'pointer',
        transition: 'background-color 120ms ease',
        '&:hover, &:focus-visible': {
          bgcolor:
            'color-mix(in srgb, var(--color-accent) 24%, var(--color-surface-elevated-strong))',
          outline: 'none',
        },
      }}
    >
      <HistoryIcon
        sx={{ fontSize: 18, color: 'var(--color-text-muted)', flexShrink: 0 }}
      />
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1,
          bgcolor: 'var(--color-surface-elevated-strong)',
          border: '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        {item.thumbnail ? (
          <Box
            component="img"
            src={item.thumbnail}
            alt=""
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <ImageNotSupportedOutlinedIcon
            sx={{ fontSize: 18, color: 'var(--color-text-muted)' }}
          />
        )}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: 'var(--color-text-strong)' }}
          noWrap
        >
          {item.title}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: 'var(--color-text-muted)', display: 'block' }}
          noWrap
        >
          {kindLabel(item.itemType)}
        </Typography>
      </Box>
    </Box>
  );
}

function SearchResultRow({
  item,
  onSelect,
}: {
  item: SearchResultItem;
  onSelect: (item: SearchResultItem) => void;
}) {
  const Icon = kindIcon(item.kind);
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onSelect(item)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(item);
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 2,
        py: 1,
        cursor: 'pointer',
        transition: 'background-color 120ms ease',
        '&:hover, &:focus-visible': {
          bgcolor:
            'color-mix(in srgb, var(--color-accent) 24%, var(--color-surface-elevated-strong))',
          outline: 'none',
        },
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1,
          bgcolor: 'var(--color-surface-elevated-strong)',
          border: '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        {item.thumbnail ? (
          <Box
            component="img"
            src={item.thumbnail}
            alt=""
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Icon sx={{ fontSize: 18, color: 'var(--color-text-muted)' }} />
        )}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: 'var(--color-text-strong)' }}
          noWrap
        >
          {item.title}
        </Typography>
        {item.subtitle && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            {item.kind === 'project' || item.kind === 'gls' ? (
              <LocationOnIcon
                sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}
              />
            ) : null}
            <Typography
              variant="caption"
              sx={{ color: 'var(--color-text-muted)' }}
              noWrap
            >
              {item.subtitle}
            </Typography>
          </Stack>
        )}
      </Box>
    </Box>
  );
}
